import React, { useEffect } from "react";
import { Route } from "react-router-dom";
import SignIn from "../pages/Auth/SignIn";

import { warn } from "../utils/logger";

// State
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { loadUser } from "../state/actions/user";
import { setInternalAuthHeader } from "../utils/axios.InternalConfig";

interface State {
  user: RouteOptions;
}

interface RouteOptions {
  loading: boolean;
  userToken?: string;
  isAuthenticated: boolean;
  loadUser: () => void;
}

/**
 * User must be logged in to view wrapped component
 * @param {boolean}   loading           Whether the user details are still loading
 * @param {boolean}   isAuthenticated   Whether the user is logged in or not
 * @param {string}    userToken         JWT of the logged in user
 * @param {function}  loadUser          Attempt to load the user using JWT
 * @param {any}       rest              Pass remaining options through to child
 * @return Route
 * @constructor
 */
const PrivateRoute = ({ loading, userToken, isAuthenticated, loadUser, ...rest }: RouteOptions) => {
  useEffect(() => {
    if (!isAuthenticated && userToken) {
      warn("Attempting to log in the user through stored token", { userToken });
      setInternalAuthHeader(userToken);
      loadUser();
    }
  }, [isAuthenticated, userToken, loadUser]);

  // If user is not authenticated once loading finishes, redirect to sign-in page
  if (!isAuthenticated && !loading) {
    warn(`[i] Unauthenticated user visited url[${window.location.pathname}]`, {
      isAuthenticated,
      loading,
    });
    return <SignIn />;
  }

  // User is authenticated, show route
  return <Route {...rest} />;
};

PrivateRoute.propTypes = {
  loading: PropTypes.bool.isRequired,
  userToken: PropTypes.string,
  isAuthenticated: PropTypes.bool.isRequired,

  // Actions
  loadUser: PropTypes.func.isRequired,
};

const mapStateToProps = (state: State) => ({
  loading: state.user.loading,
  userToken: state.user.userToken,
  isAuthenticated: state.user.isAuthenticated,
});

export default connect(mapStateToProps, { loadUser })(PrivateRoute);
